/**
 * Google Tasks List Service - Phase 4 Architecture
 *
 * Encapsulates Google Task list CRUD and list-scope helpers.
 * Extracted from GoogleTasksSyncService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from "@/services/core/service-registry";
import type { GoogleTaskList, EnableListSyncRequest } from "@/types/google-tasks";
import { validateEnableListScope, type ListScopeBinding } from "./google-tasks-scope";
import { GoogleTasksApiClient } from "./google-tasks-api-client.service";

const log = logger.createNamespace("GoogleTasksListService");

export interface IGoogleTasksListService extends IService {
  getTaskLists(userId: string): Promise<ServiceResponse<GoogleTaskList[]>>;
  enableListSync(userId: string, request: EnableListSyncRequest): Promise<ServiceResponse<void>>;
  disableListSync(userId: string, listId: string): Promise<ServiceResponse<void>>;
  getEnabledListIds(userId: string): Promise<string[]>;
  getEnabledListBindings(userId: string): Promise<ListScopeBinding[]>;
  getListScopeForImport(
    userId: string,
    listId: string
  ): Promise<{ organization_id: string; project_id: string | null; client_id: string | null }>;
}

export class GoogleTasksListService implements IGoogleTasksListService {
  public readonly name = "GoogleTasksListService";
  private readonly supabase = typedSupabase;
  private apiClient: GoogleTasksApiClient;

  constructor(apiClient: GoogleTasksApiClient) {
    this.apiClient = apiClient;
  }

  async initialize(): Promise<void> {
    log.info("Google Tasks List Service initialized");
  }

  async dispose(): Promise<void> {
    log.info("Google Tasks List Service disposed");
  }

  async getTaskLists(userId: string): Promise<ServiceResponse<GoogleTaskList[]>> {
    log.debug("Fetching task lists", { userId });

    const result = await this.apiClient.request<{ items?: GoogleTaskList[] }>(
      userId,
      "/users/@me/lists"
    );

    if (!result.success) {
      log.error("Failed to fetch task lists", { error: result.error });
      return createErrorResponse(
        result.error ?? "Failed to fetch task lists"
      );
    }

    log.success("Fetched task lists", { count: result.data?.items?.length ?? 0 });
    return createSuccessResponse(result.data?.items ?? []);
  }

  async enableListSync(
    userId: string,
    request: EnableListSyncRequest
  ): Promise<ServiceResponse<void>> {
    log.info("Enabling list sync", { userId, listId: request.listId });

    try {
      validateEnableListScope({
        organizationId: request.organizationId,
        projectId: request.projectId,
        clientId: request.clientId,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Invalid scope";
      return createErrorResponse(message);
    }

    const { data: membership, error: memberError } = await this.supabase
      .from("organization_members")
      .select("organization_id")
      .eq("user_id", userId)
      .eq("organization_id", request.organizationId)
      .maybeSingle();

    if (memberError || !membership) {
      return createErrorResponse("You are not a member of the selected organization");
    }

    const listResult = await this.apiClient.request<GoogleTaskList>(
      userId,
      `/users/@me/lists/${request.listId}`
    );

    if (!listResult.success) {
      return createErrorResponse("Task list not found in Google Tasks");
    }

    const taskList = listResult.data;

    const { error: dbError } = await this.supabase
      .from("google_task_lists")
      .upsert(
        {
          user_id: userId,
          google_task_list_id: request.listId,
          title: taskList.title,
          is_primary: request.isPrimary ?? false,
          sync_enabled: true,
          sync_direction: request.syncDirection,
          last_sync_status: "pending",
          organization_id: request.organizationId,
          project_id: request.projectId ?? null,
          client_id: request.clientId ?? null,
        },
        {
          onConflict: "user_id,google_task_list_id",
        }
      );

    if (dbError) {
      log.error("Failed to save task list", { error: dbError });
      if (dbError.code === "23505") {
        return createErrorResponse(
          "Another enabled list already uses this organization/project/client scope"
        );
      }
      return createErrorResponse("Failed to enable sync");
    }

    log.success("List sync enabled", { listId: request.listId });
    return createSuccessResponse(undefined);
  }

  async disableListSync(userId: string, listId: string): Promise<ServiceResponse<void>> {
    log.info("Disabling list sync", { userId, listId });

    const { error } = await this.supabase
      .from("google_task_lists")
      .update({ sync_enabled: false, sync_direction: "disabled" })
      .eq("user_id", userId)
      .eq("google_task_list_id", listId);

    if (error) {
      log.error("Failed to disable sync", { error });
      return createErrorResponse("Failed to disable sync");
    }

    log.success("List sync disabled", { listId });
    return createSuccessResponse(undefined);
  }

  async getEnabledListIds(userId: string): Promise<string[]> {
    const { data, error } = await this.supabase
      .from("google_task_lists")
      .select("google_task_list_id")
      .eq("user_id", userId)
      .eq("sync_enabled", true);

    if (error) {
      log.error("Failed to fetch enabled Google Task lists", { error, userId });
      return [];
    }

    return (data ?? []).map((list) => list.google_task_list_id);
  }

  async getEnabledListBindings(userId: string): Promise<ListScopeBinding[]> {
    const { data, error } = await this.supabase
      .from("google_task_lists")
      .select(
        "google_task_list_id, organization_id, project_id, client_id, sync_enabled, is_primary"
      )
      .eq("user_id", userId)
      .eq("sync_enabled", true);

    if (error) {
      log.error("Failed to fetch enabled Google Task list bindings", { error, userId });
      return [];
    }

    return (data ?? []).map((row) => ({
      google_task_list_id: row.google_task_list_id,
      organization_id: row.organization_id,
      project_id: row.project_id,
      client_id: row.client_id,
      sync_enabled: row.sync_enabled ?? false,
      is_primary: row.is_primary,
    }));
  }

  async getListScopeForImport(
    userId: string,
    listId: string
  ): Promise<{ organization_id: string; project_id: string | null; client_id: string | null }> {
    const { data, error } = await this.supabase
      .from("google_task_lists")
      .select("organization_id, project_id, client_id, sync_enabled")
      .eq("user_id", userId)
      .eq("google_task_list_id", listId)
      .maybeSingle();

    if (error || !data?.organization_id) {
      throw new Error(
        `Google Task list ${listId} has no organization scope configured. Enable sync with an org binding first.`
      );
    }

    return {
      organization_id: data.organization_id,
      project_id: data.project_id ?? null,
      client_id: data.client_id ?? null,
    };
  }
}
